/**
 * MonthEndReview - "Closing the books on [Month]"
 *
 * Full-screen, swipe-style slides for one finished month. White pages with a
 * soft single-colour gradient, one tilted postcard per slide, headlines in one
 * colour, body text in ink. Tap the right side to go on, the left side to go back.
 * Colours come from getReviewPalette(theme), so every theme gets its own family.
 */
import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, ChevronRight } from "lucide-react";
import { formatCurrency, formatCurrencyWhole } from "../../utils/formatters";
import {
  FEELINGS, buildMonthReview, getReviewPalette, keptPhrase, numberWord, inSentence,
  monthNameOf, monthShortOf, yearOf,
} from "../../utils/monthEndReview";
import { usePlan, PLAN_REVIEW } from "../../utils/plan"; // plan_08

const SERIF = "'DM Serif Display', serif";
const SANS = "'DM Sans', sans-serif";
const INK = '#2E2C33';
const SHADOW = '0 12px 30px rgba(28,27,31,0.07), 0 2px 6px rgba(28,27,31,0.04)';

// ---------------------------------------------------------------- pieces
const Headline = ({ color, size = 36, children }) => (
  <h1 style={{ margin: '14px 0 0', fontFamily: SERIF, fontWeight: 400, fontSize: size, lineHeight: 1.08, letterSpacing: '-0.015em', color }}>
    {children}
  </h1>
);

const Body = ({ children, mt = 32 }) => (
  <p style={{ margin: `${mt}px 0 0`, fontSize: 17, lineHeight: 1.5, color: INK, fontFamily: SANS }}>{children}</p>
);

const Chip = ({ emoji, size = 46 }) => (
  <span style={{ display: 'flex', width: size, height: size, borderRadius: Math.round(size / 3), background: 'rgba(255,255,255,0.65)', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size / 2) }}>
    {emoji}
  </span>
);

const Label = ({ color, mt = 16, children }) => (
  <span style={{ display: 'block', marginTop: mt, fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color }}>{children}</span>
);

const Big = ({ color, size = 76, mt = 4, children }) => (
  <span style={{ display: 'block', marginTop: mt, fontFamily: SERIF, fontSize: size, lineHeight: 1, letterSpacing: '-0.02em', color }}>{children}</span>
);

const CardNote = ({ color, mt = 14, children }) => (
  <span style={{ display: 'block', marginTop: mt, fontSize: 16, lineHeight: 1.45, color }}>{children}</span>
);

const Postcard = ({ p, rot = 0, sticker, side = 'right', pad = '24px 24px 26px', mt = 28, children }) => (
  <div style={{ position: 'relative', marginTop: mt, padding: `0 6px ${sticker ? 30 : 0}px` }}>
    <div style={{ boxSizing: 'border-box', padding: pad, borderRadius: 30, background: p.fill, boxShadow: SHADOW, transform: `rotate(${rot}deg)` }}>
      {children}
    </div>
    {sticker && (
      <div style={{ position: 'absolute', bottom: 0, [side]: 4, padding: '11px 16px', borderRadius: 18, background: '#FFFFFF', boxShadow: SHADOW, transform: `rotate(${side === 'right' ? 4 : -4}deg)`, fontSize: 15, fontWeight: 700, color: p.label, zIndex: 2 }}>
        {sticker}
      </div>
    )}
  </div>
);

const Row = ({ left, right, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 16, background: 'rgba(255,255,255,0.7)' }}>
    <span style={{ flexGrow: 1, fontSize: 15, fontWeight: 500, color: INK }}>{left}</span>
    <span style={{ fontSize: 15, fontWeight: 700, color }}>{right}</span>
  </div>
);

// ---------------------------------------------------------------- component
const MonthEndReview = ({
  monthKey,
  transactions = [],
  noSpendDays = {},
  sinkingFunds = [],
  categoryBudgets = {},
  theme = 'pink',
  onClose,
  onPinNote, // (text, nextMonthKey) => void
}) => {
  const plan = usePlan();
  const P = plan ? PLAN_REVIEW : getReviewPalette(theme);
  const r = useMemo(
    () => buildMonthReview({ monthKey, transactions, noSpendDays, sinkingFunds, categoryBudgets }),
    [monthKey, transactions, noSpendDays, sinkingFunds, categoryBudgets]
  );
  const [idx, setIdx] = useState(0);
  const [note, setNote] = useState('');

  // Lock the page behind while the review is open, then put it back exactly as it was.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const prevShort = monthShortOf(r.prevKey);
  const next = r.nextName;
  const money = formatCurrencyWhole;

  // ------------------------------------------------------------ slides
  const slides = [];

  // 1. Cover
  slides.push({
    role: 'income',
    body: (
      <>
        <div style={{ flexGrow: 1, maxHeight: plan ? 12 : 48 }} />
        {plan ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 8, flexShrink: 0 }}>
            {[
              { role: 'income', emoji: '\u{1F4B0}', label: 'Came in', value: money(r.income) },
              r.saved > 0
                ? { role: 'saved', emoji: '\u{1F331}', label: 'You kept', value: money(r.saved) }
                : { role: 'spent', emoji: '\u{1F4B3}', label: 'Spent', value: money(r.spent) },
              { role: 'leftover', emoji: '\u{1F31E}', label: 'No-spend days', value: String(r.noSpend.count) },
              { role: 'spent', emoji: '\u{1F9FE}', label: 'Transactions', value: String(r.txCount) },
            ].map((c) => (
              <div key={c.label} style={{ borderRadius: 20, padding: 14, background: P[c.role].fill }}>
                <span style={{ display: 'flex', width: 32, height: 32, borderRadius: 999, background: 'rgba(255,255,255,0.75)', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{c.emoji}</span>
                <span style={{ display: 'block', marginTop: 10, fontSize: 12.5, color: '#55525F' }}>{c.label}</span>
                <span style={{ display: 'block', marginTop: 2, fontSize: 19, fontWeight: 700, letterSpacing: '-0.5px', color: P[c.role].deep }}>{c.value}</span>
              </div>
            ))}
          </div>
        ) : (
        <div style={{ position: 'relative', height: 392, margin: '0 -4px', flexShrink: 0 }}>
          {[
            { role: 'income', rot: -6, top: 10, left: 2, emoji: '\u{1F4B0}', label: 'Came in', value: money(r.income), z: 1 },
            r.saved > 0
              ? { role: 'saved', rot: 5, top: 96, left: 132, emoji: '\u{1F331}', label: 'You kept', value: money(r.saved), z: 2 }
              : { role: 'spent', rot: 5, top: 96, left: 132, emoji: '\u{1F4B3}', label: 'Spent', value: money(r.spent), z: 2 },
            { role: 'leftover', rot: -3, top: 226, left: 22, emoji: '\u{1F31E}', label: 'No-spend days', value: String(r.noSpend.count), z: 3 },
          ].map((c) => (
            <div key={c.label} style={{ position: 'absolute', top: c.top, left: c.left, width: 212, boxSizing: 'border-box', padding: '20px 20px 22px', borderRadius: 26, background: P[c.role].fill, boxShadow: SHADOW, transform: `rotate(${c.rot}deg)`, zIndex: c.z }}>
              <Chip emoji={c.emoji} size={42} />
              <Label color={P[c.role].label} mt={14}>{c.label}</Label>
              <Big color={P[c.role].deep} size={36} mt={4}>{c.value}</Big>
            </div>
          ))}
        </div>
        )}
        <Headline color={P.income.deep} size={48}>Closing the books on {r.monthName}</Headline>
        <Body mt={12}>{r.days} days and {r.txCount} transactions later. Let&rsquo;s see how you did!</Body>
        <div style={{ flexGrow: 1, minHeight: 24 }} />
        <button
          type="button"
          onClick={() => setIdx(1)}
          style={{ width: '100%', padding: '20px 24px', border: 0, borderRadius: 22, background: P.ui.button, color: '#FFFFFF', fontSize: 17, fontWeight: 600, fontFamily: SANS, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: P.ui.shadow, flexShrink: 0 }}
        >
          Let&rsquo;s go
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </>
    ),
    anchored: true,
    corner: `${r.monthName} ${r.year}`,
  });

  // 2. Four numbers
  slides.push({
    role: 'leftover',
    body: (
      <>
        <Headline color={P.leftover.deep}>{r.monthName} in four numbers</Headline>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px 14px', marginTop: 34, padding: '0 4px' }}>
          {[
            { role: 'income', rot: -2, emoji: '\u{1F4B0}', label: 'Came in', value: r.income },
            { role: 'spent', rot: 2, emoji: '\u{1F4B3}', label: 'Spent', value: r.spent },
            { role: 'saved', rot: 1.5, emoji: '\u{1F331}', label: 'Saved & inv.', value: r.saved },
            { role: 'leftover', rot: -2, emoji: '\u{1F31E}', label: 'Leftover', value: r.leftover },
          ].map((c) => (
            <div key={c.label} style={{ boxSizing: 'border-box', padding: '16px 16px 18px', borderRadius: 24, background: P[c.role].fill, boxShadow: SHADOW, transform: `rotate(${c.rot}deg)` }}>
              <Chip emoji={c.emoji} size={36} />
              <Label color={P[c.role].label} mt={12}>{c.label}</Label>
              <Big color={P[c.role].deep} size={24} mt={4}>{formatCurrency(c.value)}</Big>
            </div>
          ))}
        </div>
        <Body mt={34}>
          Here&rsquo;s the big picture before we get into the fun stuff. Leftover is what you didn&rsquo;t spend or save by the end of the month.
        </Body>
      </>
    ),
  });

  // 3. What you kept
  if (r.saved > 0) {
    const phrase = keptPhrase(r.saved, r.income);
    const funds = r.fundsTotal;
    const inv = r.invested;
    const split = [
      funds > 0 ? `${money(funds)} into your funds` : null,
      inv > 0 ? `${money(inv)} invested` : null,
    ].filter(Boolean).join(', ');
    let closing = null;
    if (r.bestEver) closing = 'Your best month yet. Look at you!';
    else if (r.bestSince) closing = `Your best month since ${monthNameOf(r.bestSince)}. Look at you!`;
    else if (r.savedDiff > 0) closing = `That’s ${money(r.savedDiff)} more than ${r.prevName}!`;
    slides.push({
      role: 'saved',
      body: (
        <>
          <Headline color={P.saved.deep}>
            {phrase ? `You kept ${phrase} of what came in!` : `You kept ${money(r.saved)}!`}
          </Headline>
          <Postcard p={P.saved} rot={-2.5} sticker={r.savedDiff > 0 ? `+${money(r.savedDiff)} vs ${prevShort}!` : null}>
            <Chip emoji={'\u{1F331}'} />
            <Label color={P.saved.label} mt={18}>Saved &amp; invested</Label>
            <Big color={P.saved.deep} size={80}>{money(r.saved)}</Big>
            {split && <CardNote color={P.saved.label}>{split}.</CardNote>}
          </Postcard>
          {closing && <Body>{closing}</Body>}
        </>
      ),
    });
  }

  // 4. No-spend days
  if (r.noSpend.count > 0) {
    const n = r.noSpend.count;
    const diff = n - r.noSpend.prevCount;
    const vsPrev = !r.hasPrev
      ? null
      : diff > 0 ? `That’s ${numberWord(diff).toLowerCase()} more than ${r.prevName}.`
      : diff < 0 ? `${r.prevName} had ${r.noSpend.prevCount}, so there’s room to beat it in ${next}.`
      : `Same as ${r.prevName}, nice and steady.`;
    const run = r.noSpend.run;
    const runText = run.len >= 2 ? ` Your longest run was ${monthShortOf(r.monthKey)} ${run.start} to ${run.end}.` : '';
    const days = new Set(r.noSpend.days);
    slides.push({
      role: 'spent',
      body: (
        <>
          <Headline color={P.spent.deep}>
            {n === 1 ? 'One day with no spending!' : `${numberWord(n)} days with no spending!`}
          </Headline>
          <Postcard p={P.spent} rot={2} pad="20px 20px 22px" sticker={run.len >= 2 ? `${run.len} days in a row!` : null} side="left">
            <Label color={P.spent.label} mt={0}>{r.monthName}</Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6, marginTop: 12 }}>
              {Array.from({ length: r.days }, (_, i) => i + 1).map((d) => (
                <div
                  key={d}
                  style={{
                    aspectRatio: '1 / 1', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
                    background: days.has(d) ? '#FFFFFF' : 'rgba(255,255,255,0.35)',
                    color: P.spent.label, fontWeight: days.has(d) ? 700 : 400, opacity: days.has(d) ? 1 : 0.55,
                  }}
                >
                  {d}
                </div>
              ))}
            </div>
          </Postcard>
          {(vsPrev || runText) && <Body>{`${vsPrev || ''}${runText}`.trim()}</Body>}
        </>
      ),
    });
  }

  // 5. Spent less
  if (r.drop) {
    const d = r.drop;
    slides.push({
      role: 'saved',
      body: (
        <>
          <Headline color={P.saved.deep}>You spent {money(-d.diff)} less on {inSentence(d.name)}!</Headline>
          <Postcard p={P.saved} rot={-2} sticker="Nicely done!">
            <Chip emoji={d.emoji} />
            <Label color={P.saved.label} mt={18}>{d.name}</Label>
            <Big color={P.saved.deep} size={80}>&minus;{d.pct}%</Big>
            <CardNote color={P.saved.label}>{formatCurrency(d.now)} this month, {formatCurrency(d.was)} in {r.prevName}.</CardNote>
          </Postcard>
          <Body>
            {d.countNow < d.countWas
              ? `${d.countNow} transaction${d.countNow === 1 ? '' : 's'}, down from ${d.countWas}.`
              : `Less money, even with ${d.countNow} transaction${d.countNow === 1 ? '' : 's'}.`}
            {r.biggestMove === 'drop' ? ' That was your biggest change all month!' : ''}
          </Body>
        </>
      ),
    });
  }

  // 6. Spent more
  if (r.rise) {
    const u = r.rise;
    let closing;
    if (!u.budget) closing = `There was no ${inSentence(u.name)} budget set. Maybe add one for ${next}?`;
    else if (u.now > u.budget) closing = `That’s ${money(u.now - u.budget)} over your ${inSentence(u.name)} budget. Worth a look for ${next}.`;
    else closing = `Still inside your ${inSentence(u.name)} budget, so no stress!`;
    slides.push({
      role: 'leftover',
      body: (
        <>
          <Headline color={P.leftover.deep}>You spent {money(u.diff)} more on {inSentence(u.name)}</Headline>
          <Postcard p={P.leftover} rot={2} sticker="Hey, it happens." side="left">
            <Chip emoji={u.emoji} />
            <Label color={P.leftover.label} mt={18}>{u.name}</Label>
            <Big color={P.leftover.deep} size={u.pct === null ? 64 : 80}>{u.pct === null ? `+${money(u.diff)}` : `+${u.pct}%`}</Big>
            <CardNote color={P.leftover.label}>
              {u.was > 0
                ? `${formatCurrency(u.now)} this month, ${formatCurrency(u.was)} in ${r.prevName}.`
                : `New this month. Nothing here in ${r.prevName}.`}
            </CardNote>
          </Postcard>
          <Body>{closing}</Body>
        </>
      ),
    });
  }

  // 7. Biggest day
  if (r.biggestDay) {
    const b = r.biggestDay;
    const sticker = `Sounds like a busy ${b.weekday}!`;
    slides.push({
      role: 'spent',
      body: (
        <>
          <Headline color={P.spent.deep}>Your biggest day was {b.weekday}, {monthShortOf(r.monthKey)} {b.day}</Headline>
          <Postcard p={P.spent} rot={-2} pad="22px 22px 24px" sticker={sticker}>
            <Label color={P.spent.label} mt={0}>Spent that day</Label>
            <Big color={P.spent.deep} size={60}>{formatCurrency(b.total)}</Big>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 18 }}>
              {b.top.map((c) => (
                <Row key={c.category} left={`${c.emoji} ${c.name}`} right={formatCurrency(c.total)} color={P.spent.label} />
              ))}
            </div>
          </Postcard>
          <Body>
            {b.count} transaction{b.count === 1 ? '' : 's'} in one day{b.mostly ? `, mostly ${inSentence(b.mostly)}` : ''}.
            {r.expenseCount > 1 ? ` Across the whole month you averaged ${formatCurrency(r.avgBuy)} a buy.` : ''}
          </Body>
        </>
      ),
    });
  }

  // 8. How you felt (feeling tags). With no tags yet, a gentle nudge to start.
  if (r.expenseCount > 0) {
    const f = r.feelings;
    if (f.tagged > 0) {
      const byId = {};
      f.rows.forEach((row) => { byId[row.id] = row; });
      const regrets = byId.regret.count;
      const sticker = regrets === 0
        ? 'No regrets!'
        : regrets <= 5 ? `Only ${numberWord(regrets).toLowerCase()} regret${regrets === 1 ? '' : 's'}!` : null;
      const good = byId.needed.amount + byId.worth.amount;
      const goodPct = f.taggedAmount > 0 ? Math.round((good / f.taggedAmount) * 100) : 0;
      const bits = [];
      if (goodPct > 0) bits.push(`${goodPct}% of what you tagged was needed or worth it.`);
      if (byId.treat.count > 0) bits.push(`Treats came to ${money(byId.treat.amount)}.`);
      if (byId.impulse.count > 0) bits.push(`Impulse buys added up to ${money(byId.impulse.amount)}.`);
      slides.push({
        role: 'leftover',
        body: (
          <>
            <Headline color={P.leftover.deep}>How your spending felt</Headline>
            <Postcard p={P.leftover} rot={-2} pad="20px 20px 22px" sticker={sticker}>
              <Label color={P.leftover.label} mt={0}>
                You tagged {f.tagged} of {f.total} buy{f.total === 1 ? '' : 's'}
              </Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
                {f.rows.filter((row) => row.count > 0).map((row) => (
                  <Row
                    key={row.id}
                    left={`${row.emoji} ${row.label}`}
                    right={`${row.count} · ${money(row.amount)}`}
                    color={P.leftover.label}
                  />
                ))}
              </div>
            </Postcard>
            {bits.length > 0 && <Body>{bits.join(' ')}</Body>}
          </>
        ),
      });
    } else {
      slides.push({
        role: 'leftover',
        body: (
          <>
            <Headline color={P.leftover.deep}>How did your spending feel?</Headline>
            <Postcard p={P.leftover} rot={-2} pad="22px 18px 24px">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {FEELINGS.map((x) => (
                  <div key={x.id} style={{ width: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{x.emoji}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: P.leftover.label, whiteSpace: 'nowrap' }}>{x.label}</span>
                  </div>
                ))}
              </div>
              <CardNote color={P.leftover.label} mt={18}>
                When you add an expense, tap how it felt. It only takes a second!
              </CardNote>
            </Postcard>
            <Body>
              Start tagging your buys and your next review will show what was worth it, what was a treat and what you&rsquo;d skip next time.
            </Body>
          </>
        ),
      });
    }
  }

  // 9. Funds
  if (r.funds.length > 0) {
    const cardRoles = ['income', 'spent', 'leftover'];
    const rots = [-1.5, 1.5, -1];
    const extra = r.invested > 0 ? `, plus ${money(r.invested)} invested` : '';
    const proj = r.projection
      ? ` Keep this pace and ${r.projection.name} is fully funded by ${monthNameOf(r.projection.key)} ${yearOf(r.projection.key)}!`
      : ' Every bit counts!';
    slides.push({
      role: 'saved',
      body: (
        <>
          <Headline color={P.ui.button}>
            {r.fundCount === 1 ? 'You made progress on a fund!' : `You made progress on ${numberWord(r.fundCount).toLowerCase()} funds!`}
          </Headline>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 30, padding: '0 4px' }}>
            {r.funds.map((f, i) => {
              const p = P[cardRoles[i % 3]];
              const pct = f.goal > 0 ? Math.min(100, Math.round((f.balance / f.goal) * 100)) : 0;
              return (
                <div key={f.id} style={{ marginLeft: i === 1 ? 10 : i === 2 ? 2 : 0, boxSizing: 'border-box', padding: '14px 16px 16px', borderRadius: 22, background: p.fill, boxShadow: SHADOW, transform: `rotate(${rots[i]}deg)` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Chip emoji={f.emoji} size={38} />
                    <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: p.label, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      <span style={{ fontSize: 13, color: p.label }}>
                        {f.goal > 0 ? `${money(f.balance)} of ${money(f.goal)}` : `${money(f.balance)} saved`}
                      </span>
                    </div>
                    <span style={{ fontFamily: SERIF, fontSize: 24, color: p.deep }}>+{money(f.net)}</span>
                  </div>
                  {f.goal > 0 && (
                    <div style={{ height: 7, marginTop: 12, borderRadius: 4, background: 'rgba(255,255,255,0.7)' }}>
                      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: p.deep }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Body mt={34}>{money(r.fundsTotal)} across your funds{extra}.{proj}</Body>
        </>
      ),
    });
  }

  // 10. Note for next month
  const ideas = [];
  if (r.rise && r.rise.was > 0) ideas.push(`Cap ${inSentence(r.rise.name)} at ${money(Math.ceil(r.rise.was / 10) * 10)}`);
  ideas.push(`Keep ${Math.max(r.noSpend.count + 1, 4)} no-spend days`);
  if (r.saved > 0) ideas.push(`Save ${money(Math.ceil((r.saved * 1.1) / 50) * 50)}`);
  slides.push({
    role: 'income',
    noNav: true,
    body: (
      <>
        <Headline color={P.income.deep}>Leave yourself a note for {next}</Headline>
        <Postcard p={P.income} rot={0} pad="20px 20px 22px">
          <label htmlFor="mer-note" style={{ display: 'block', fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: P.income.label }}>
            {next}&rsquo;s notes
          </label>
          <textarea
            id="mer-note"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Something to remember next month..."
            style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 12, padding: '12px 14px', border: 0, borderRadius: 16, resize: 'none', background: 'rgba(255,255,255,0.75)', fontFamily: SANS, fontSize: 16, lineHeight: 1.45, color: INK, outline: 'none' }}
          />
        </Postcard>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 22 }}>
          {ideas.map((t) => (
            <button key={t} type="button" onClick={() => setNote(t)} style={{ minHeight: 40, padding: '9px 14px', border: 0, borderRadius: 20, background: P.income.fill, fontSize: 14, fontWeight: 500, color: P.income.label, fontFamily: SANS }}>
              {t}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 'auto', paddingTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            disabled={!note.trim()}
            onClick={() => { if (onPinNote) onPinNote(note.trim(), r.nextKey); if (onClose) onClose(); }}
            style={{ width: '100%', padding: '20px 24px', border: 0, borderRadius: 22, background: P.ui.button, color: '#FFFFFF', fontSize: 17, fontWeight: 600, fontFamily: SANS, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: note.trim() ? P.ui.shadow : 'none', opacity: note.trim() ? 1 : 0.45 }}
          >
            Pin it to {next}
          </button>
          <button type="button" onClick={onClose} style={{ width: '100%', minHeight: 44, border: 0, background: 'transparent', color: '#8a8a8a', fontSize: 15, fontWeight: 500, fontFamily: SANS }}>
            Maybe later
          </button>
        </div>
      </>
    ),
    anchored: true,
  });

  const total = slides.length;
  const i = Math.min(idx, total - 1);
  const slide = slides[i];

  // Tap left third = back, anywhere else = forward. Taps on buttons / the note box are left alone.
  const onTap = (e) => {
    if (e.target.closest('button, textarea, input, a, label')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const leftSide = e.clientX - rect.left < rect.width * 0.32;
    if (leftSide) setIdx(Math.max(0, i - 1));
    else if (!slide.noNav && i < total - 1) setIdx(i + 1);
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${r.monthName} month-end review`}
      className={"fixed inset-0" + (plan ? " wy-plan-review" : "")}
      style={{ zIndex: 10000, background: `linear-gradient(180deg, ${P.ui.bgTop} 0%, ${P.ui.bgBottom} 100%)`, fontFamily: SANS, color: INK }}
    >
      <div
        onClick={onTap}
        style={{
          maxWidth: 480, height: '100%', margin: '0 auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
          padding: 'calc(env(safe-area-inset-top, 0px) + 14px) 0 calc(env(safe-area-inset-bottom, 0px) + 24px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 24px' }}>
          <div style={{ display: 'flex', gap: 6, flexGrow: 1 }}>
            {slides.map((_, n) => (
              <span key={n} style={{ height: 6, width: n === i ? 22 : 6, borderRadius: 3, background: n === i ? P.ui.button : 'rgba(0,0,0,0.12)', transition: 'width 0.3s ease' }} />
            ))}
          </div>
          {slide.corner && <span style={{ fontSize: 13, fontWeight: 500, color: '#8a8a8a' }}>{slide.corner}</span>}
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ width: 44, height: 44, marginRight: -12, border: 0, background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
          >
            <X className="w-4 h-4" style={{ color: '#8a8a8a' }} />
          </button>
        </div>
        <div key={i} style={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '0 24px 24px', animation: 'merFade 0.25s ease' }}>
          {!slide.anchored && <div style={{ flexGrow: 1, maxHeight: 70 }} />}
          {slide.body}
          {!slide.anchored && <div style={{ flexGrow: 2 }} />}
        </div>
      </div>
      <style>{'@keyframes merFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }'}</style>
    </div>,
    document.body
  );
};

export default MonthEndReview;
